import React, { useRef, useState, useLayoutEffect, useEffect } from "react";
import GoogleMapReact from "google-map-react";
import useSupercluster from "use-supercluster";
import locationMarkerIcon from "../../../../img/location-marker.svg";
import selectedLocationMarkerIcon from "../../../../img/location-marker-selected.svg";
import Explorer from "../../../data-containers/explorer";
import CONFIG from "../../../lib/config";
import PrintError from "../../atoms/print-error";
import StatusInline from "../../atoms/status-inline";
import mapStyles from "./mapStyles";

const PinnedLocations = ({ setSelectedLocation, isSelectedLocation, key }) => (
  <div onClick={setSelectedLocation} key={key}>
    <img src={isSelectedLocation ? selectedLocationMarkerIcon : locationMarkerIcon} className="locationMarker" />
  </div>
);

export default function Map() {
  const { GOOGLE_MAPS_API_KEY } = CONFIG.values;
  const [selectedLocation, updateSelectedLocation] = useState("");

  const mapRef = useRef();
  const [bounds, setBounds] = useState(null);
  const [points, setPoints] = useState([]);
  const [zoom, setZoom] = useState(4);
  const [presentLocation, setPresentLocation] = useState({});
  const [showWordBubble, setShowWordBubble] = useState(false);
  const [mapsLib, setMapsLib] = useState();

  const {
    activeTab,
    setSelectedMapLocation,
    getExplorerItems
  } = Explorer.useContainer();
  const locationsState = getExplorerItems();

  const setSelectedLocation = (loc) => {
    const {results} = locationsState;
    if(results.length){
      const prLoc = results.find(({id}) => id===loc);
      if(prLoc) {
        const [lat, lng] = prLoc.geocode.split(",");
        prLoc.lat = lat;
        prLoc.lng = lng;
        setPresentLocation(prLoc);
        setShowWordBubble(true);
      }
    }
    updateSelectedLocation(loc);
  };
  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 75, maxZoom: 18 },
  });

  const defaultProps = {
    center: {
      lat: 37.09024,
      lng: -95.712891,
    },
    zoom: 4,
  };

  useLayoutEffect(() => {
    if (locationsState?.results?.length) {
      setPoints(
        locationsState?.results.map((location, index) => {
          if (location?.geocode) {
            let splitGeocode = location?.geocode.split(",");
            return {
              type: "Feature",
              properties: {
                cluster: false,
                id: location.id,
                index,
                isSelectedLocation: selectedLocation === location.id,
              },
              geometry: {
                type: "Point",
                coordinates: [
                  parseFloat(splitGeocode[1]),
                  parseFloat(splitGeocode[0]),
                ],
              },
            };
          } else {
            return {
              type: "Feature",
              properties: { cluster: null, id: "" },
              geometry: {
                type: "Point",
                coordinates: [0, 0],
              },
            };
          }
        })
      );
    }
  }, [locationsState, selectedLocation]);

  useEffect(() => {
    // reset selected location when tab changes
    if (selectedLocation) {
      setSelectedLocation("");
      setSelectedMapLocation({
        id: "",
        index: undefined,
      });
    }
  }, [activeTab]);

  useEffect(
    () => {
      try {
        if(mapsLib && mapRef.current && locationsState?.results?.length){
          const { results } = locationsState;
          const bounds = new mapsLib.LatLngBounds();
          let foundLocation = false;
          for(const { geocode } of results){
            if(geocode){
              const coords = geocode.split(',').map(parseFloat);
              if(coords.length && coords.every(n => !isNaN(n))){
                const latLng = new mapsLib.LatLng(...coords);
                if(latLng){
                  foundLocation = true;
                  bounds.extend(latLng);
                }  
              }
            }
          }          
          if(foundLocation){
            mapRef.current.fitBounds(bounds);
            // for a single location, don't zoom too much 
            if(results.length === 1){
              mapRef.current.setZoom(16);
            }  
          }
        }
      }
      catch(err){
        console.error(err);
      }
    },
    [locationsState, mapRef, mapsLib]
  );

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <StatusInline type="error">
        <PrintError error="Missing Google Maps API key" />
      </StatusInline>
    );
  }

  return (
    <div className="container">
      <GoogleMapReact
        bootstrapURLKeys={{ key: GOOGLE_MAPS_API_KEY }}
        defaultCenter={defaultProps.center}
        options={{
          styles: mapStyles,
        }}
        defaultZoom={defaultProps.zoom}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => {
          mapRef.current = map;
          setMapsLib(maps);
        }}
        onChange={({ zoom, bounds }) => {
          setZoom(zoom);
          setBounds([
            bounds.nw.lng,
            bounds.se.lat,
            bounds.se.lng,
            bounds.nw.lat,
          ]);
        }}
      >
        {selectedLocation && showWordBubble &&
          <div
          className="googleMapsWordBubble"
          // lat={latitude}
          // lng={longitude}
          lat={presentLocation.lat}
          lng={presentLocation.lng}
          > <span className={"clickToClose"} onClick={() => setShowWordBubble(false)}>x</span>
            <p>{presentLocation.label}</p>
            <p>{presentLocation.address}</p>
            <p>{`${presentLocation.city}, ${presentLocation.state} ${presentLocation.postcode}`}</p>
          </div>
        }
        {clusters.length > 0 &&
          clusters.map((cluster, i) => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const {
              cluster: isCluster,
              point_count: pointCount,
              id,
              index,
              isSelectedLocation,
            } = cluster.properties;
            if (isCluster) {
              return (
                <div
                  className="cluster-marker"
                  onClick={() => {
                    const expansionZoom = Math.min(
                      supercluster.getClusterExpansionZoom(cluster.id),
                      20
                    );
                    mapRef.current.setZoom(expansionZoom);
                    mapRef.current.panTo({ lat: latitude, lng: longitude });
                  }}
                  key={`cluster-${cluster.id}`}
                  lat={latitude}
                  lng={longitude}
                >
                  {pointCount}
                </div>
              );
            } else if (isCluster === false) {
              return (
                <PinnedLocations
                  lat={latitude}
                  lng={longitude}
                  key={i}
                  setSelectedLocation={() => {
                    setSelectedLocation(id);
                    setSelectedMapLocation({
                      id,
                      index,
                    });
                  }}
                  isSelectedLocation={isSelectedLocation}
                />
              );
            }
          })}
      </GoogleMapReact>
    </div>
  );
}
